import React, { ReactElement, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { RightFlex, ModalBackdrop, ModalContainer, styledScrollBars } from "src/gantt/components/common/StyledComponents";
import { DragHandle } from "src/gantt/components/filters/FiltersCommon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { 
  EDITBOX_BORDER
} from "src/gantt/helpers/Const";
import { JSONEditor } from "@json-editor/json-editor";
import { GenericButton, SecondaryButton } from "../common/Buttons";
import configSchema from "src/backend/configuration/externalConfiguration.schema.json";
import "spectre.css/dist/spectre.min.css";
import "spectre.css/dist/spectre-icons.min.css";
import { getConfigFromNVSAsync, setConfigToNVSAsync } from "src/backend/services/NamedValueStorageService";
import { getConfig } from "src/backend/helpers/urlHelper";
import { getConfigAsync } from "src/backend/configuration/ConfigurationProvider";
import { Dispatch, iRootState } from "src/rematch/store";
import { IExternalConfiguration } from "src/backend/interfaces/configuration/IExternalConfiguration";
import { IBaseConfiguration } from "src/backend/interfaces/configuration/IBaseConfiguration";

const CONFIGURATIONFORM_MODAL_WIDTH = 1100;
const CONFIGURATIONFORM_MODAL_HEIGHT = 700;

const CONFIGURATIONFORM_MODAL_PADDING = 24;
const CONFIGURATIONFORM_MODAL_HEADER_HEIGHT = 40;
const CONFIGURATIONFORM_MODAL_FOOTER_HEIGHT = 62;
const CONFIGURATIONFORM_MODAL_CONTENT_HEIGHT = CONFIGURATIONFORM_MODAL_HEIGHT - CONFIGURATIONFORM_MODAL_HEADER_HEIGHT - CONFIGURATIONFORM_MODAL_FOOTER_HEIGHT - 2 * CONFIGURATIONFORM_MODAL_PADDING;

const DRAG_HANDLE_CLASSNAME = "dragHandle";

const ConfigurationPage = styled.div`
  border: 2px solid ${EDITBOX_BORDER};
  background: white;
  padding:  ${CONFIGURATIONFORM_MODAL_PADDING}px;
  width: ${CONFIGURATIONFORM_MODAL_WIDTH}px;
  height: ${CONFIGURATIONFORM_MODAL_HEIGHT}px;
`;

const ConfigurationHeaderText = styled.div`
  font-weight: bold;
  font-size: 20px;
  margin-bottom: 20px;
`;

const ConfigurationHeader = styled.div`
  height: ${CONFIGURATIONFORM_MODAL_HEADER_HEIGHT}px;
`;

const ConfigurationEditor = styled.div`
  height: ${CONFIGURATIONFORM_MODAL_CONTENT_HEIGHT}px;
  overflow: auto;
  ${styledScrollBars}
`;

const ConfigurationFooter = styled.div`
  height: ${CONFIGURATIONFORM_MODAL_FOOTER_HEIGHT}px;
  padding-top: 20px;
`;

export const ConfigurationForm = (): ReactElement => {

  const { t } = useTranslation();
  const dispatch = useDispatch<Dispatch>();
  const httpClient = useSelector((state: iRootState) => state.common.httpClient);
  const dragRef = useRef<HTMLDivElement>();

  const toggleVisibility = () => {
    dispatch.UI.toggleShowConfigurationForm();
  }

  let editor = null;

  useEffect(() => {
    async function save(baseConfiguration: IBaseConfiguration, externalConfiguration: IExternalConfiguration) {
      try {
        const _configuration = await getConfigAsync(httpClient, externalConfiguration, baseConfiguration);
        await setConfigToNVSAsync(baseConfiguration, externalConfiguration);
        const reload = window.confirm("You need to reload application before changes are applied. \nDo you want to reload now?")
        if(reload) {
          window.location.reload();
        }
      } catch(e) {
        alert(e);
      }
    }
    async function initializeEditor() {
      const baseConfiguration = await getConfig();
      const externalConfiguration = await getConfigFromNVSAsync(baseConfiguration);

      if(editor) {
        editor.destroy();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      editor = new JSONEditor(document.getElementById('editor_holder'),{
        schema: configSchema,
        startval: externalConfiguration,
        theme: 'spectre',
        iconlib: 'spectre',
        array_controls_top: true,
        display_required_only: true,
      });
      editor.on('change',() => {
        const errors = editor.validate();
        (document.getElementById('submit') as HTMLFormElement).disabled = errors.length ? true : false;
      });
      document.getElementById('submit').addEventListener('click',function() {
        const errors = editor.validate();
        if(errors.length) {
          alert("fix errors before saving");
        } else {
          save(baseConfiguration, editor.getValue());
        } 
      });
    }
    initializeEditor();
    return () => {
      if(editor) {
        editor.destroy();
        editor = null;
      }
    }
  },[httpClient]);

  return (
    <div>
      <Draggable nodeRef={dragRef} handle={`.${DRAG_HANDLE_CLASSNAME}`}>
        <ModalContainer ref={dragRef} style={{ top: "50%", marginTop: -CONFIGURATIONFORM_MODAL_HEIGHT / 2, left: "50%", marginLeft: -CONFIGURATIONFORM_MODAL_WIDTH / 2 }}>
          <ConfigurationPage>
            <ConfigurationHeader>
              <DragHandle><ConfigurationHeaderText>{t("configuration.header")}</ConfigurationHeaderText></DragHandle>
            </ConfigurationHeader>
            <ConfigurationEditor id="editor_holder" />
            <ConfigurationFooter>
              <RightFlex>
                <SecondaryButton width={136} style={{ marginRight: "20px" }} onClick={toggleVisibility}>
                  {t("command.close")}
                </SecondaryButton>
                <GenericButton id="submit" width={136}>
                  {t("command.save")}
                </GenericButton>
              </RightFlex>
            </ConfigurationFooter>
          </ConfigurationPage>
        </ModalContainer>
      </Draggable>
      <ModalBackdrop onClick={toggleVisibility}></ModalBackdrop>
    </div>
  );

}